import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../../config/api';
import AlertDialog from '../Common/AlertDialog';

const DocumentGenerationModal = ({ open, onClose, classes, onGenerate }) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [customColumns, setCustomColumns] = useState(['']);
  const [defaultColumns, setDefaultColumns] = useState([
    { name: "Student Name", enabled: true },
    { name: "Roll Number", enabled: true },
    { name: "Parent's Contact Number", enabled: true },
  ]);
  const [loading, setLoading] = useState(false);
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setSelectedClass('');
      setCustomColumns(['']);
      setDefaultColumns([
        { name: "Student Name", enabled: true },
        { name: "Roll Number", enabled: true },
        { name: "Parent's Contact Number", enabled: true },
      ]);
    }
  }, [open]);

  const handleCustomColumnChange = (index, value) => {
    const newColumns = [...customColumns];
    newColumns[index] = value;
    setCustomColumns(newColumns);
  };

  const handleAddCustomColumn = () => {
    setCustomColumns([...customColumns, '']);
  };

  const handleRemoveCustomColumn = (index) => {
    const newColumns = customColumns.filter((_, i) => i !== index);
    setCustomColumns(newColumns);
  };

  const toggleDefaultColumn = (index) => {
    const newDefaults = [...defaultColumns];
    newDefaults[index].enabled = !newDefaults[index].enabled;
    setDefaultColumns(newDefaults);
  };

  const handleGenerate = async () => {
    if (!selectedClass) {
      setAlertDialog({
        open: true,
        title: 'Validation Error',
        message: 'Please select a class',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      // Prepare data for backend
      const requestData = {
        class_id: selectedClass,
        default_columns: defaultColumns,
        custom_columns: customColumns,
      };

      // Call backend to generate PDF
      const response = await fetch(API_ENDPOINTS.STUDENTS_GENERATE_PDF, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate PDF' }));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = 'Students_List.pdf';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (onGenerate) {
        onGenerate();
      }
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: error.message || 'Failed to generate PDF. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PdfIcon color="primary" />
          <Typography variant="h6">Generate Student Document</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Class Selection */}
          <FormControl fullWidth required>
            <InputLabel>Select Class</InputLabel>
            <Select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              label="Select Class"
            >
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}{cls.section ? ` - ${cls.section}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          {/* Default Columns */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Default Columns
            </Typography>
            <Stack spacing={1}>
              {defaultColumns.map((col, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={col.enabled}
                      onChange={() => toggleDefaultColumn(index)}
                      color="primary"
                    />
                  }
                  label={col.name}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      opacity: col.enabled ? 1 : 0.5,
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Divider />

          {/* Custom Columns */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Custom Columns (for offline use)
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddCustomColumn}
                variant="outlined"
              >
                Add Column
              </Button>
            </Box>
            <Stack spacing={2}>
              {customColumns.map((col, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label={`Column ${index + 1}`}
                    value={col}
                    onChange={(e) => handleCustomColumnChange(index, e.target.value)}
                    placeholder="Enter column name (leave empty for blank column)"
                    size="small"
                    helperText={!col.trim() ? "Empty column will be included in PDF" : ""}
                  />
                  <IconButton
                    onClick={() => handleRemoveCustomColumn(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            {customColumns.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No custom columns added. Click "Add Column" to create columns (with or without names) for offline use.
              </Typography>
            )}
            {customColumns.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                Tip: You can leave column names empty to create blank columns for manual entry offline.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
          disabled={!selectedClass || loading}
        >
          {loading ? 'Generating...' : 'Generate PDF'}
        </Button>
      </DialogActions>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Dialog>
  );
};

export default DocumentGenerationModal;

